(() => {
  if (window.ContactFaqsSection) {
    window.ContactFaqsSection.init(document);
    return;
  }

  const SECTION_SELECTOR = '[data-contact-faqs]';
  const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
  const MOBILE_STICKY_CLEARANCE = 8;
  const HEADER_SELECTOR = [
    '#header-component',
    '#header-component > .header__row',
    '#header-component .header-logo',
    '.header-section',
    '.shopify-section-group-header-group',
    '#shopify-section-header',
    '.shopify-section-header-sticky',
    '.section-header',
    '.header-wrapper',
    'sticky-header',
    '[data-header]',
    '.header-announcement-bar__section',
    '.header-announcement-bar__section .announcement',
    '.announcement-bar-section',
    'body > header'
  ].join(',');

  class ContactFaqs {
    constructor(section) {
      this.section = section;
      this.scroller = section.querySelector('[data-faqs-scroll]');
      this.groups = Array.from(section.querySelectorAll('[data-faqs-group]'));
      this.targetButtons = Array.from(section.querySelectorAll('[data-faqs-target]'));
      this.dropdowns = Array.from(section.querySelectorAll('[data-faqs-dropdown]'));
      this.desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);
      this.abortController = new AbortController();
      this.spyLocked = false;
      this.spyLockTimer = 0;
      this.scrollFrame = 0;
      this.headerObserver = null;
      this.mobileGroupScrollFrame = 0;
      this.restoreScrollBehavior = null;
      this.mobileAccordionAnimations = new WeakMap();
    }

    init() {
      if (typeof window.initCustomAccordion === 'function') {
        window.initCustomAccordion(this.section);
      }

      this.bindMobileAccordion();
      this.bindCategoryNavigation();
      this.bindDropdowns();
      this.bindScrollSpy();
      this.observeHeaderState();
      this.updateMobileStickyOffset();
      this.updateActiveNavigation();
    }

    bindMobileAccordion() {
      const { signal } = this.abortController;

      this.section.addEventListener('click', event => {
        if (this.desktopMedia.matches) return;

        const question = event.target.closest('.accordion .question');
        if (!question || !this.section.contains(question)) return;

        const item = question.closest('li');
        const accordion = question.closest('.accordion');
        if (!item || !accordion) return;

        event.stopImmediatePropagation();

        const shouldOpen = !item.classList.contains('open');
        accordion.querySelectorAll(':scope > ul > li.open').forEach(openItem => {
          if (openItem !== item) this.setMobileAccordionItem(openItem, false);
        });
        this.setMobileAccordionItem(item, shouldOpen);
      }, { signal, capture: true });
    }

    setMobileAccordionItem(item, isOpen) {
      const answer = item.querySelector(':scope > .answer');
      if (!answer) return;

      const previous = this.mobileAccordionAnimations.get(answer);
      if (previous) {
        window.clearTimeout(previous.timer);
        answer.removeEventListener('transitionend', previous.onEnd);
      }

      const token = {};
      const currentHeight = answer.getBoundingClientRect().height;

      answer.style.transition = 'none';
      answer.style.overflow = 'hidden';
      answer.style.display = 'block';
      answer.style.height = `${currentHeight}px`;
      item.classList.toggle('open', isOpen);

      void answer.offsetHeight;

      const finish = () => {
        const active = this.mobileAccordionAnimations.get(answer);
        if (!active || active.token !== token) return;

        answer.removeEventListener('transitionend', active.onEnd);
        window.clearTimeout(active.timer);
        answer.style.removeProperty('transition');
        answer.style.removeProperty('overflow');

        if (item.classList.contains('open')) {
          answer.style.display = 'block';
          answer.style.height = 'auto';
        } else {
          answer.style.display = 'none';
          answer.style.height = '0px';
        }

        this.mobileAccordionAnimations.delete(answer);
        this.requestLayoutUpdate();
      };

      const onEnd = event => {
        if (event.target === answer && event.propertyName === 'height') finish();
      };
      const timer = window.setTimeout(finish, 380);
      this.mobileAccordionAnimations.set(answer, { token, timer, onEnd });
      answer.addEventListener('transitionend', onEnd);

      window.requestAnimationFrame(() => {
        const active = this.mobileAccordionAnimations.get(answer);
        if (!active || active.token !== token) return;

        answer.style.transition = 'height 300ms ease';
        answer.style.height = isOpen ? `${answer.scrollHeight}px` : '0px';
      });
    }

    bindCategoryNavigation() {
      const { signal } = this.abortController;

      this.targetButtons.forEach(button => {
        button.addEventListener('click', () => {
          const group = this.groups.find(item => item.dataset.faqsGroup === button.dataset.faqsTarget);
          if (!group) return;

          this.setActiveNavigation(button.dataset.faqsTarget);
          this.closeDropdowns();
          this.lockScrollSpy();
          this.scrollToGroup(group);
        }, { signal });
      });
    }

    bindDropdowns() {
      const { signal } = this.abortController;

      this.dropdowns.forEach(dropdown => {
        const toggle = dropdown.querySelector('[data-faqs-dropdown-toggle]');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
          const shouldOpen = toggle.getAttribute('aria-expanded') !== 'true';
          this.closeDropdowns(dropdown);
          this.setDropdownState(dropdown, shouldOpen);
        }, { signal });
      });

      document.addEventListener('click', event => {
        if (!this.section.contains(event.target)) {
          this.closeDropdowns();
        }
      }, { signal });

      this.section.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        this.closeDropdowns();
      }, { signal });
    }

    bindScrollSpy() {
      const { signal } = this.abortController;
      const update = () => this.requestLayoutUpdate();

      if (this.scroller) {
        this.scroller.addEventListener('scroll', update, { signal, passive: true });
      }

      window.addEventListener('scroll', update, { signal, passive: true });
      window.addEventListener('resize', update, { signal, passive: true });

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', update, { signal, passive: true });
        window.visualViewport.addEventListener('scroll', update, { signal, passive: true });
      }
    }

    observeHeaderState() {
      const headers = document.querySelectorAll('#header-component');
      if (!headers.length) return;

      this.headerObserver = new MutationObserver(() => this.requestLayoutUpdate());
      headers.forEach(header => {
        this.headerObserver.observe(header, {
          attributes: true,
          attributeFilter: ['data-sticky-state', 'data-scroll-direction', 'style', 'class']
        });
      });
    }

    setDropdownState(dropdown, isOpen) {
      const toggle = dropdown.querySelector('[data-faqs-dropdown-toggle]');
      const menu = dropdown.querySelector('[data-faqs-dropdown-menu]');
      if (!toggle || !menu) return;

      dropdown.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));
      menu.hidden = !isOpen;

      this.requestLayoutUpdate();
    }

    closeDropdowns(exceptDropdown = null) {
      this.dropdowns.forEach(dropdown => {
        if (dropdown !== exceptDropdown) {
          this.setDropdownState(dropdown, false);
        }
      });
    }

    lockScrollSpy() {
      this.spyLocked = true;
      window.clearTimeout(this.spyLockTimer);
      this.spyLockTimer = window.setTimeout(() => {
        this.spyLocked = false;
        this.requestLayoutUpdate();
      }, 800);
    }

    requestLayoutUpdate() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.updateMobileStickyOffset();

        if (!this.spyLocked) {
          this.updateActiveNavigation();
        }

        this.scrollFrame = 0;
      });
    }

    updateMobileStickyOffset() {
      if (this.desktopMedia.matches) {
        this.section.style.removeProperty('--faqs-mobile-sticky-top');
        return;
      }

      const headerBottom = this.getVisibleHeaderBottom();
      const stickyTop = headerBottom > 0 ? headerBottom + MOBILE_STICKY_CLEARANCE : 0;
      this.section.style.setProperty('--faqs-mobile-sticky-top', `${Math.ceil(stickyTop)}px`);
    }

    getVisibleHeaderBottom() {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const candidates = Array.from(document.querySelectorAll(HEADER_SELECTOR));
      let visibleBottom = 0;

      candidates.forEach(element => {
        if (this.section.contains(element)) return;

        const style = window.getComputedStyle(element);
        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          Number.parseFloat(style.opacity) === 0
        ) {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.top >= viewportHeight) return;

        const isActiveHeader = element.matches(
          '#header-component[sticky="always"], #header-component[data-sticky-state="active"]'
        );

        if (rect.top > 1 && !isActiveHeader && !this.hasTopAnchoredAncestor(element)) return;

        visibleBottom = Math.max(visibleBottom, rect.bottom);
      });

      return Math.min(Math.max(visibleBottom, 0), 240);
    }

    hasTopAnchoredAncestor(element) {
      let current = element.parentElement;

      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const rect = current.getBoundingClientRect();

        if (
          (style.position === 'sticky' || style.position === 'fixed') &&
          rect.top <= 1 &&
          rect.bottom > 0
        ) {
          return true;
        }

        current = current.parentElement;
      }

      return false;
    }

    scrollToGroup(group) {
      if (this.desktopMedia.matches && this.scroller) {
        const top = group.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top + this.scroller.scrollTop;
        this.scroller.scrollTo({ top, behavior: 'smooth' });
        return;
      }

      this.cancelMobileGroupScroll();

      const root = document.documentElement;
      const body = document.body;
      const rootScrollBehavior = root.style.scrollBehavior;
      const bodyScrollBehavior = body.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      body.style.scrollBehavior = 'auto';

      this.restoreScrollBehavior = () => {
        root.style.scrollBehavior = rootScrollBehavior;
        body.style.scrollBehavior = bodyScrollBehavior;
        this.restoreScrollBehavior = null;
      };

      const startTime = performance.now();
      const stabilize = () => {
        this.updateMobileStickyOffset();
        this.alignMobileGroup(group);

        if (performance.now() - startTime < 600) {
          this.mobileGroupScrollFrame = window.requestAnimationFrame(stabilize);
          return;
        }

        this.mobileGroupScrollFrame = 0;
        this.restoreScrollBehavior?.();
      };

      this.mobileGroupScrollFrame = window.requestAnimationFrame(stabilize);
    }

    alignMobileGroup(group) {
      const stickyNavigation = this.getVisibleMobileNavigation();
      const navigationBottom = stickyNavigation?.getBoundingClientRect().bottom || 0;
      const desiredTop = navigationBottom + 16;
      const target = group.querySelector('.faqs__group-title') || group;
      const difference = target.getBoundingClientRect().top - desiredTop;

      if (Math.abs(difference) > 1) {
        window.scrollBy({ top: difference, behavior: 'auto' });
      }
    }

    cancelMobileGroupScroll() {
      if (this.mobileGroupScrollFrame) {
        window.cancelAnimationFrame(this.mobileGroupScrollFrame);
        this.mobileGroupScrollFrame = 0;
      }

      this.restoreScrollBehavior?.();
    }

    updateActiveNavigation() {
      if (!this.groups.length || !this.targetButtons.length) return;

      let activeGroup = this.groups[0];

      if (this.desktopMedia.matches && this.scroller) {
        const threshold = this.scroller.scrollTop + Math.min(this.scroller.clientHeight * 0.4, 200);

        this.groups.forEach(group => {
          const groupTop = group.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top + this.scroller.scrollTop;
          if (groupTop <= threshold) activeGroup = group;
        });
      } else {
        const stickyNavigation = this.getVisibleMobileNavigation();
        const threshold = (stickyNavigation ? stickyNavigation.getBoundingClientRect().bottom : 0) + 32;

        this.groups.forEach(group => {
          if (group.getBoundingClientRect().top <= threshold) activeGroup = group;
        });
      }

      this.setActiveNavigation(activeGroup.dataset.faqsGroup);
    }

    setActiveNavigation(index) {
      this.targetButtons.forEach(button => {
        const isActive = button.dataset.faqsTarget === String(index);
        button.classList.toggle('active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    getVisibleMobileNavigation() {
      return Array.from(this.section.querySelectorAll('.faqs__navigation--mobile'))
        .find(navigation => navigation.offsetParent !== null) || null;
    }

    destroy() {
      this.abortController.abort();
      this.headerObserver?.disconnect();
      window.clearTimeout(this.spyLockTimer);
      this.cancelMobileGroupScroll();

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      this.section.style.removeProperty('--faqs-mobile-sticky-top');
      delete this.section.contactFaqsInstance;
    }
  }

  function getSections(root) {
    const sections = [];

    if (root instanceof Element && root.matches(SECTION_SELECTOR)) {
      sections.push(root);
    }

    if (root.querySelectorAll) {
      sections.push(...root.querySelectorAll(SECTION_SELECTOR));
    }

    return sections;
  }

  function init(root = document) {
    getSections(root).forEach(section => {
      if (section.contactFaqsInstance) return;

      const instance = new ContactFaqs(section);
      section.contactFaqsInstance = instance;
      instance.init();
    });
  }

  function destroy(root) {
    getSections(root).forEach(section => {
      section.contactFaqsInstance?.destroy();
    });
  }

  window.ContactFaqsSection = { init, destroy };
  window.initContactFaqs = init;

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));

  if (document.readyState !== 'loading') {
    init(document);
  }
})();
